import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useActiveChatStore } from '#modules/chat/model/activeChatStore';
import { useChatSettingsStore } from '#modules/chat/model/chatSettingsStore';
import { useChatTypingStore } from '#modules/chat/model/chatTypingStore';
import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { updateAppBadge } from '#features/notifications/utils/appBadge';
import { getCachedNativeToken } from '#features/api/utils/nativeAuth';
import { applyWsCallEvent } from '#features/calls/utils/applyWsCallEvent';
import { playNotificationSound } from '#features/chats/utils/playNotificationSound';
import { i18n } from '#features/i18n/i18n';
import { getPathnamePrefix } from '#features/i18n/utils/getPathnamePrefix';
import { useBellAnimationStore } from '#features/notifications/model/bellAnimationStore';
import {
  shouldSuppressChatToast,
  shouldSuppressToastByGame,
} from '#features/realtime/toastFilters';
import { applyWsChatTypingEvent } from '#features/realtime/utils/applyWsChatTypingEvent';
import {
  parseWsMessage,
  type WsMessage,
  type WsOutboundData,
} from '#features/realtime/wsMessageParser';
import { toaster } from '#features/ui/components/Toaster';
import { envConfig } from '#constants/envConfig';

// Backend enrichment substitutes this placeholder when the actor has no uploaded
// avatar. Treat it as "no avatar" so the toast falls back to the coloured initial
// (consistent with avatars elsewhere) instead of rendering a blank image.
const DEFAULT_AVATAR_FILENAME = 'default-avatar.png';

const toAvatarUrl = (path: string | null | undefined): string | undefined => {
  if (!path) return undefined;
  if (path.endsWith(DEFAULT_AVATAR_FILENAME)) return undefined;
  if (path.startsWith('http')) return path;
  return `${envConfig.MEDIA_URL}/mh/user/${path}`;
};

const WS_PATH = '/ws';
const PING_INTERVAL_MS = 25_000;
const BASE_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const WS_CLOSE_CODE_RECONNECT = 1000;
const WS_CLOSE_CODE_PING_FAILED = 3001;
const WS_CLOSE_CODE_SOCKET_ERROR = 3002;

type WsQueryKey = readonly unknown[];

const isSameQueryKey = (left: WsQueryKey, right: WsQueryKey): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
};

const isQueryKeyPrefix = (queryKey: readonly unknown[], partial: WsQueryKey): boolean => {
  if (partial.length > queryKey.length) {
    return false;
  }

  return partial.every((value, index) => queryKey[index] === value);
};

const toWsProtocol = (protocol: string): 'ws:' | 'wss:' => (protocol === 'https:' ? 'wss:' : 'ws:');

const toWsUrlFromHttp = (urlValue: string): string | null => {
  if (!envConfig.IN_BROWSER) {
    return null;
  }

  try {
    const parsed = new URL(urlValue, window.location.origin);
    parsed.protocol = toWsProtocol(parsed.protocol);
    parsed.pathname = WS_PATH;
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString();
  } catch {
    return null;
  }
};

const normalizeWsUrl = (input?: string): string | null => {
  if (!envConfig.IN_BROWSER) {
    return null;
  }

  const value = input?.trim();
  if (!value) {
    return null;
  }

  if (value.startsWith('ws://') || value.startsWith('wss://')) {
    return value;
  }

  if (value.startsWith('http://') || value.startsWith('https://')) {
    return toWsUrlFromHttp(value);
  }

  if (value.startsWith('/')) {
    return `${toWsProtocol(window.location.protocol)}//${window.location.host}${value}`;
  }

  return null;
};

// The native shell can't set a Cookie header on the WS handshake (and has no
// shared cookie jar anyway), so it passes the JWT as a `?token=` query param,
// which teamplay-ws reads as a fallback after the cookie. Web never hits this
// (IS_NATIVE folds to false; the web client also strips the query server-side).
const withNativeToken = (wsUrl: string | null): string | null => {
  if (!wsUrl || !envConfig.IS_NATIVE) {
    return wsUrl;
  }
  const token = getCachedNativeToken();
  if (!token) {
    return wsUrl;
  }
  const separator = wsUrl.includes('?') ? '&' : '?';
  return `${wsUrl}${separator}token=${encodeURIComponent(token)}`;
};

const resolveWsUrl = (): string | null => {
  if (!envConfig.IN_BROWSER) {
    return null;
  }

  const explicit = normalizeWsUrl(envConfig.WS_URL);
  if (explicit) {
    return withNativeToken(explicit);
  }

  const byApiUrl = envConfig.API_URL ? toWsUrlFromHttp(envConfig.API_URL) : null;
  if (byApiUrl) {
    return withNativeToken(byApiUrl);
  }

  return withNativeToken(
    `${toWsProtocol(window.location.protocol)}//${window.location.host}${WS_PATH}`
  );
};

let wsInstance: WebSocket | null = null;

const reconnectCallbacks = new Set<() => void>();

export function onWsReconnect(callback: () => void): () => void {
  reconnectCallbacks.add(callback);
  return () => {
    reconnectCallbacks.delete(callback);
  };
}

export function sendWsMessage(message: object): void {
  if (wsInstance?.readyState === WebSocket.OPEN) {
    wsInstance.send(JSON.stringify(message));
  }
}

export const useRealtimeNotifications = () => {
  const isAuth = useIsAuth();
  const queryClient = useQueryClient();
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const pingTimerRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);

  const wsUrl = resolveWsUrl();

  useEffect(() => {
    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const clearPingTimer = () => {
      if (pingTimerRef.current !== null) {
        window.clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
    };

    const closeSocket = () => {
      const socket = socketRef.current;
      socketRef.current = null;
      wsInstance = null;
      if (
        socket &&
        (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
      ) {
        socket.close(WS_CLOSE_CODE_RECONNECT, 'reconnect');
      }
    };

    if (!envConfig.IN_BROWSER || !isAuth || !wsUrl) {
      useChatTypingStore.getState().clearAllTyping();
      clearReconnectTimer();
      clearPingTimer();
      closeSocket();
      return;
    }

    const activeWsUrl = wsUrl;
    let isDisposed = false;

    const cancelAndInvalidateQueries = (keys: WsQueryKey[]) => {
      const uniqueKeys = keys.filter(
        (key, index) => keys.findIndex((candidate) => isSameQueryKey(candidate, key)) === index
      );

      if (uniqueKeys.length === 0) {
        return;
      }

      const matchesQuery = (queryKey: readonly unknown[]) =>
        uniqueKeys.some((key) => isQueryKeyPrefix(queryKey, key));

      // Только invalidate, без предварительного cancelQueries. Отмена била по запросу, которого
      // в этот момент ждёт лоадер маршрута: на медленном канале WS-событие о свежесозданном посте
      // приходило, пока /posts/$postId ещё await'ил ensureQueryData, промис лоадера режектился
      // (CancelledError), дерево поста схлопывалось и разворачивалось через Suspense — и это
      // запускало каскад, добивавший страницу до React #185 (прод, 04–05.08.2026).
      // invalidate сам корректно перезапустит активные запросы, ничего не обрывая.
      void queryClient
        .invalidateQueries({
          predicate: (query) => matchesQuery(query.queryKey),
        })
        .catch(() => undefined);
    };

    const buildLocalePath = (path: string): string => {
      const prefix = getPathnamePrefix(i18n.locale);
      return prefix ? `/${prefix}${path}` : path;
    };

    const playWsNotificationSound = () => {
      const { soundEnabled } = useChatSettingsStore.getState();
      if (soundEnabled) {
        playNotificationSound();
      }
    };

    const invalidateByType = (
      type: string,
      data: WsOutboundData | null,
      actorUserId?: string | null
    ) => {
      const postId = data?.parentId;
      const keys: WsQueryKey[] = [];

      switch (type) {
        // SESSION_UPDATED is the broadcast signal (fires for member join/leave/status changes).
        // It reaches every online client — importantly the actor themselves, whose per-user
        // SESSION_MEMBER_JOINED is suppressed (SKIPPED_SELF_ACTOR). This is what refreshes the
        // invitee's own open page after they accept an invite from Discord.
        case 'SESSION_UPDATED':
        case 'SESSION_CREATED':
        case 'SESSION_MEMBER_JOINED':
        case 'SESSION_MEMBER_LEFT':
        case 'SESSION_COMPLETED':
        case 'SESSION_DISCORD_ACTIVATED':
        case 'DISCORD_VOICE_MEMBERS_CHANGED':
          if (postId)
            keys.push(['post', postId], ['post-sessions', postId], ['discordRoom', postId]);
          // Состав изменился — значит изменился и ответ на вопрос «могу ли я занять место по
          // ссылке». Без этого экран приглашения держал «место уже заняли» до перезагрузки
          // страницы, хотя место освободилось у человека на глазах: статус приглашения живёт
          // своим ключом и на обновление заявки не реагирует.
          keys.push(['invite-link']);
          keys.push(
            ['my-sessions'],
            ['archived-sessions'],
            ['sessions'],
            ['discover-sessions'],
            ['discover-listings'],
            ['discover-favourites'],
            ['profile-user-sessions'],
            ['sessions-count'],
            ['posts'],
            ['feed']
          );
          break;

        // Голосовая комната сессии: кадры обновляют счётчик «N в голосе» на карточках и состав в
        // виджете разговора. Тоста нет ни у одного из них, кроме SESSION_VOICE_STARTED — у него
        // есть ws_template с ключом, и остальных участников зовут в комнату именно им.
        case 'SESSION_VOICE_STARTED':
        case 'VOICE_PARTICIPANT_JOINED':
        case 'VOICE_PARTICIPANT_LEFT':
        case 'VOICE_PARTICIPANT_REMOVED':
        case 'VOICE_ROOM_CLOSED': {
          const sessionId = data?.targetId;
          if (sessionId) keys.push(['voiceRoom', sessionId]);
          if (postId) keys.push(['post', postId], ['post-sessions', postId]);
          keys.push(['my-sessions'], ['sessions'], ['discover-sessions'], ['posts'], ['feed']);
          break;
        }

        case 'SESSION_MEMBER_INVITED':
        case 'SESSION_INVITE_DECLINED':
        case 'SESSION_INVITE_REVOKED':
        case 'SESSION_CANCEL':
        case 'SESSION_REMINDER':
          if (postId) keys.push(['post', postId], ['post-sessions', postId]);
          keys.push(['my-sessions'], ['sessions']);
          break;

        case 'POST_BUMP_REMINDER':
          if (postId) keys.push(['post', postId]);
          break;

        case 'POST_CREATED':
        case 'POST_UPDATED':
        case 'POST_DELETED':
          // Broadcast events fan out to everyone subscribed on Discover / Post page;
          // they need to refresh the feed lists and the post-detail caches.
          if (postId) keys.push(['post', postId], ['post-sessions', postId]);
          keys.push(
            ['posts'],
            ['discover-listings'],
            ['discover-sessions'],
            ['discover-favourites'],
            ['my-post-game-ids'],
            ['feed']
          );
          break;

        case 'COMMENT_CREATED':
        case 'COMMENT_REPLY':
          if (postId) keys.push(['post', postId]);
          keys.push(['comments']);
          break;

        case 'CHAT_MESSAGE': {
          // ['chats'] refreshes the sidebar list preview (last-message text,
          // timestamp, unread badge) — without it the preview is stale until
          // a manual reload while the user is reading another conversation.
          keys.push(['chats'], ['notifications'], ['chatsUnreadExist'], ['chatsUnreadCount']);
          /*
           * Своё же сообщение переписку НЕ перезапрашивает.
           *
           * Событие приходит и отправителю, и до этого оно сбрасывало ленту у того, кто только
           * что нажал «отправить». Отправка уже вписала сообщение в список сама и на своё место;
           * перезапрос приезжал следом и подменял список целиком, а на стыке двух состояний
           * лента успевала дёрнуться: сообщение на мгновение вставало выше и затем прыгало вниз.
           * Именно этот второй кадр и был виден глазом.
           *
           * Собеседнику перезапрос по-прежнему нужен: у него этого сообщения нет вовсе, и взять
           * его больше неоткуда. Всё остальное (список чатов, счётчики) обновляется у обоих:
           * там перезапрос ничего не ломает и держит превью свежим.
           */
          const isOwnMessage = Boolean(actorUserId) && actorUserId === getCurrentUserId();
          if (postId && !isOwnMessage) keys.push(['chatMessages', postId]);
          // Сообщение пришло — значит собеседник допечатал. Само по себе это очевидно, но
          // «печатает» так не гаснет: событие остановки приходит тем же типом CHAT_TYPING и
          // без признака, поэтому получатель продлевает подсказку вместо того, чтобы снять её,
          // и она висит ещё три секунды поверх только что доставленного сообщения.
          if (postId) {
            useChatTypingStore.getState().clearTyping(postId);
          }
          break;
        }

        case 'CHAT_READ':
        case 'CHAT_REACTION':
        // Legacy aliases — keep until backend rename ships everywhere.
        case 'CHAT_MESSAGES_READ':
        case 'CHAT_MESSAGE_REACTION':
          // CHAT_READ updates the unread-counter shown in the sidebar list;
          // CHAT_REACTION can move counters on the last-message preview too.
          // Both need the sidebar list to refetch.
          keys.push(['chats'], ['chatsUnreadExist'], ['chatsUnreadCount']);
          if (postId) keys.push(['chatMessages', postId]);
          break;

        case 'CHAT_DELETED':
          keys.push(['chats']);
          if (postId) keys.push(['chatMessages', postId]);
          break;

        case 'NOTIFICATION_READ':
          keys.push(['notifications']);
          break;
      }

      if (keys.length > 0) {
        cancelAndInvalidateQueries(keys);
      }
    };

    const getCurrentUserId = (): string | null => {
      const meData = queryClient.getQueryData<{ __typename?: string; id?: string } | null>(['me']);
      return meData && meData.__typename === 'CurrentUser' ? (meData.id ?? null) : null;
    };

    const getCurrentUserGameIds = (): string[] | null => {
      const meData = queryClient.getQueryData<{
        __typename?: string;
        gameIds?: (string | null)[] | null;
      } | null>(['me']);
      if (!meData || meData.__typename !== 'CurrentUser') return null;
      return (meData.gameIds ?? []).filter((g): g is string => typeof g === 'string');
    };

    // Signal events ride on the same Plan 2 pipeline and carry a messageKey for
    // schema reasons (ws_template.message_key_code is NOT NULL), but they must
    // not produce a toast — they update typing dots / read ticks / reaction
    // counters via invalidation + side effects instead.
    const SIGNAL_EVENT_TYPES = new Set<string>([
      'CHAT_TYPING',
      'CHAT_READ',
      'CHAT_REACTION',
      'CHAT_MESSAGES_READ', // legacy aliases
      'CHAT_MESSAGE_REACTION',
    ]);

    // POST_UPDATED / POST_DELETED fan out to every connected user on every post
    // change and aren't interesting as a popup — keep them muted (cache
    // invalidation in the switch above still keeps Discover fresh). POST_CREATED
    // is NOT muted: it's a "new LFG post" announcement, surfaced as a toast but
    // game-filtered (see shouldSuppressToastByGame) just like SESSION_CREATED.
    const TOAST_MUTED_BROADCAST_EVENT_TYPES = new Set<string>(['POST_UPDATED', 'POST_DELETED']);

    // Comment events reach two audiences: the post author (per-user delivery, carries
    // hasNotification=true from the sibling BELL rule) and every viewer of the post
    // (POST_COMMENTS_TOPIC broadcast, hasNotification=false — for live inline updates
    // only). Toast just the former; viewers get the comment inline, not a popup.
    const COMMENT_EVENT_TYPES = new Set<string>(['COMMENT_CREATED', 'COMMENT_REPLY']);

    const showNotificationToast = (message: WsMessage) => {
      const { messageKey, params, parentId, targetId } = message.data ?? {};
      if (!messageKey) return;
      if (SIGNAL_EVENT_TYPES.has(message.type)) return;
      if (TOAST_MUTED_BROADCAST_EVENT_TYPES.has(message.type)) return;
      if (COMMENT_EVENT_TYPES.has(message.type) && !message.data?.hasNotification) return;

      // Don't show toast about own actions (e.g. "you joined session" when you clicked Join)
      // Exception: POST_BUMP_REMINDER is a self-notification by design
      const isSelfReminder = message.type === 'POST_BUMP_REMINDER';
      const currentUserId = getCurrentUserId();
      if (!isSelfReminder && currentUserId && message.actor?.userId === currentUserId) return;

      // Broadcast session announcements (SESSION_CREATED) reach every online
      // user; only toast players who have this game in their profile.
      if (shouldSuppressToastByGame(message.type, message.data?.gameId, getCurrentUserGameIds())) {
        return;
      }

      // Chat: don't toast/sound for the conversation the user is already viewing,
      // when the tab is backgrounded, or when the user muted this chat — the
      // unread badge still updates.
      const tabHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      const chatMuted = useChatSettingsStore.getState().isChatMuted(parentId);
      if (
        shouldSuppressChatToast({
          type: message.type,
          parentId,
          activeChatId: useActiveChatStore.getState().activeChatId,
          tabHidden,
          muted: chatMuted,
        })
      ) {
        return;
      }

      const text = i18n._(messageKey, params ?? {});

      const isInvite = message.type === 'SESSION_MEMBER_INVITED';
      // «Команда собралась в голосе» ценно ровно сейчас: даём восемь секунд вместо пяти, чтобы
      // человек успел прочитать и нажать, но не превращаем в баннер — разговор идёт без него.
      const isVoiceStarted = message.type === 'SESSION_VOICE_STARTED';

      let href: string | undefined;
      if (message.type === 'CHAT_MESSAGE' && parentId) {
        href = buildLocalePath(`/dashboard/chats/${parentId}`);
      } else if (message.type === 'POST_DELETED') {
        // The post no longer exists — a link to /posts/{id} would 404.
        href = undefined;
      } else if (message.type === 'CHAT_DELETED') {
        // The chat is gone; deep-linking to /dashboard/chats/{id} would 404.
        // Instead the toast points at the actor's profile (the peer who deleted),
        // so the recipient can quickly open their page if they want to follow up.
        const actorId = message.actor?.userId;
        href = actorId ? buildLocalePath(`/players/${actorId}`) : undefined;
      } else if (parentId) {
        href = buildLocalePath(`/posts/${parentId}`);
      }

      toaster.create({
        type: 'info',
        duration: isInvite ? 10_000 : isVoiceStarted ? 8_000 : 5_000,
        title: text,
        meta: {
          ...(href ? { href } : undefined),
          avatar: toAvatarUrl(message.actor?.avatar),
          avatarName: message.actor?.displayName,
          ...(isInvite && targetId
            ? { sessionInvite: { sessionId: targetId, postId: parentId ?? undefined } }
            : undefined),
        },
      });

      playWsNotificationSound();
    };

    /**
     * Снять с экрана уведомления этого разговора.
     *
     * <p>Работает только пока жива хотя бы одна вкладка: закрытой снимать нечем, и там
     * уведомление доживёт до клика — как у любого веб-приложения.
     */
    const dismissNotifications = async (tag?: string | null) => {
      if (!tag || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
        return;
      }
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        const shown = (await registration?.getNotifications({ tag })) ?? [];
        shown.forEach((notification) => notification.close());
        if (shown.length > 0) {
          await updateAppBadge(registration);
        }
      } catch {
        // Уведомление просто останется висеть до клика — не повод ронять обработку кадра.
      }
    };

    const handleSideEffects = (message: WsMessage) => {
      // 1:1 calls: frames carry messageKey=null (never toasted by the generic path) and drive
      // the global CallOverlay via the call store. currentUserId disambiguates multi-tab cases
      // (answered/declined on another device).
      //
      // Routed by prefix rather than by an explicit case list, because a frame nobody
      // dispatches is invisible: CALL_MOVED shipped with a handler in applyWsCallEvent and no
      // case here, so a call moved to the desktop left the phone in the LiveKit room —
      // publishing its microphone — until someone hung up (prod, call 1ecc5449, 18.08.2026).
      // The cases below only add the effects that are specific to one outcome.
      if (message.type.startsWith('CALL_')) {
        applyWsCallEvent(message, getCurrentUserId());
      }

      switch (message.type) {
        // «Убери уведомление, его уже прочитали» — приходит, когда человек открыл разговор на
        // другом устройстве. По вебсокету, а не пушем: Chrome требует, чтобы каждое доставленное
        // push-сообщение что-нибудь показало, и закрытие уже висящего за показ не считает —
        // вместо исчезновения человек увидел бы «сайт обновлён в фоновом режиме».
        case 'PUSH_DISMISS': {
          void dismissNotifications(message.data?.params?.tag ?? message.data?.parentId);
          break;
        }
        case 'CALL_MISSED': {
          // The callee gets a regular top-right notification toast (same style as
          // the rest of the WS feed) with a deep link into the chat to call back.
          const missedCurrentUserId = getCurrentUserId();
          const chatId = message.data?.parentId;
          if (
            missedCurrentUserId &&
            message.actor?.userId &&
            message.actor.userId !== missedCurrentUserId
          ) {
            toaster.create({
              type: 'info',
              duration: 8_000,
              title: i18n._('notification.call_missed', {
                user: message.actor.displayName || 'Player',
              }),
              meta: {
                ...(chatId ? { href: buildLocalePath(`/dashboard/chats/${chatId}`) } : undefined),
                avatar: toAvatarUrl(message.actor.avatar),
                avatarName: message.actor.displayName,
              },
            });
            playWsNotificationSound();
          }
          // The finished call left a system card in the thread — refresh the feed.
          queryClient.invalidateQueries({ queryKey: ['chatMessages'] });
          queryClient.invalidateQueries({ queryKey: ['chats'] });
          break;
        }
        case 'USER_FOLLOWED':
        case 'USER_FOLLOW_MUTUAL':
        // USER_UNFOLLOWED уведомлением не является и никому, кроме самого отписавшегося,
        // не приходит: оно существует только чтобы его вторая открытая вкладка перестала
        // показывать «Вы подписаны». Тоста по нему не будет — актор совпадает с получателем.
        case 'USER_UNFOLLOWED': {
          // Связь изменил ДРУГОЙ человек, поэтому пересчитать её локально нечем: мутация,
          // которая обычно и сбрасывает эти ключи, здесь не выполнялась вовсе.
          //
          // Сбрасываем и списки, и счётчики: подписавшийся переезжает между секциями
          // («новые подписчики» → «друзья»), а бейдж на пункте меню считается отдельным
          // запросом. Обновить одно и не обновить другое — значит показать экран, который
          // спорит сам с собой.
          queryClient.invalidateQueries({ queryKey: ['follow-list'] });
          queryClient.invalidateQueries({ queryKey: ['follow-counts'] });
          // Открытый профиль: кнопка-статус и счётчик подписчиков живут под ['follow', seed],
          // где seed — id или отображаемое имя. Какое из двух открыто сейчас, отсюда не видно,
          // поэтому сбрасываем всё семейство — запрос ровно один и стоит дёшево.
          queryClient.invalidateQueries({ queryKey: ['follow'] });
          break;
        }
        case 'CALL_REJECTED':
        case 'CALL_CANCELLED':
        case 'CALL_ENDED':
          // Terminal outcomes write a call card into the chat thread.
          queryClient.invalidateQueries({ queryKey: ['chatMessages'] });
          queryClient.invalidateQueries({ queryKey: ['chats'] });
          break;
        case 'CHAT_TYPING':
          if (message.data?.parentId && message.actor?.userId) {
            applyWsChatTypingEvent(
              message.data.parentId,
              message.actor.userId,
              message.data.params?.typing
            );
          }
          break;
        case 'CHAT_DELETED':
          if (message.data?.parentId) {
            useChatTypingStore.getState().clearTyping(message.data.parentId);
            // If the user is currently viewing this chat, navigate away to
            // /dashboard/chats — the detail route would 404 on next refetch.
            if (typeof window !== 'undefined') {
              const path = window.location.pathname;
              const deletedChatId = message.data.parentId;
              if (path.includes(`/dashboard/chats/${deletedChatId}`)) {
                const localeMatch = path.match(/^\/([a-z]{2})\//);
                const prefix = localeMatch ? `/${localeMatch[1]}` : '';
                window.location.assign(`${prefix}/dashboard/chats`);
              }
            }
          }
          break;
      }
    };

    const handleIncomingMessage = (raw: string) => {
      const message = parseWsMessage(raw);
      if (!message) return;

      // 1. Query invalidation by type
      invalidateByType(message.type, message.data, message.actor?.userId);

      // 2. Toast (if has messageKey)
      if (message.data?.messageKey) {
        showNotificationToast(message);
      }

      // 3. Bell animation (if persistent notification created)
      if (message.data?.hasNotification) {
        useBellAnimationStore.getState().triggerWiggle();
        cancelAndInvalidateQueries([['notifications'], ['hasUnreadNotifications']]);
      }

      // 4. Type-specific side effects
      handleSideEffects(message);
    };

    function scheduleReconnect() {
      if (isDisposed) {
        return;
      }

      clearReconnectTimer();

      const attempt = reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = attempt;
      const exponentialDelay = BASE_RECONNECT_DELAY_MS * 2 ** Math.min(attempt, 5);
      const delayMs = Math.min(exponentialDelay, MAX_RECONNECT_DELAY_MS);

      reconnectTimerRef.current = window.setTimeout(() => {
        connect();
      }, delayMs);
    }

    function connect() {
      if (isDisposed) {
        return;
      }

      closeSocket();
      clearPingTimer();

      const socket = new WebSocket(activeWsUrl);
      socketRef.current = socket;
      wsInstance = socket;

      socket.onopen = () => {
        reconnectAttemptRef.current = 0;
        clearReconnectTimer();
        clearPingTimer();

        // Always fire callbacks — on first connect and reconnect alike.
        // Child components register subscriptions via onWsReconnect before
        // this effect creates the socket (React fires child effects first),
        // so the initial subscribe message from sendWsMessage is silently
        // dropped while wsInstance is still null. Firing callbacks here
        // ensures subscriptions are sent as soon as the socket is open.
        reconnectCallbacks.forEach((cb) => cb());

        pingTimerRef.current = window.setInterval(() => {
          const current = socketRef.current;
          if (!current || current.readyState !== WebSocket.OPEN) {
            return;
          }

          try {
            current.send('ping');
          } catch {
            current.close(WS_CLOSE_CODE_PING_FAILED, 'ping_failed');
          }
        }, PING_INTERVAL_MS);
      };

      socket.onmessage = (event) => {
        if (typeof event.data !== 'string') {
          return;
        }

        handleIncomingMessage(event.data);
      };

      socket.onclose = () => {
        clearPingTimer();
        scheduleReconnect();
      };

      socket.onerror = () => {
        socket.close(WS_CLOSE_CODE_SOCKET_ERROR, 'socket_error');
      };
    }

    connect();

    return () => {
      isDisposed = true;
      useChatTypingStore.getState().clearAllTyping();
      clearReconnectTimer();
      clearPingTimer();
      closeSocket();
    };
  }, [isAuth, queryClient, wsUrl]);
};
